import React from 'react';
import { Navigation, CheckCircle2, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import type { StopItem } from './types';

interface TodayStopsListProps {
  stops: StopItem[];
}

export const TodayStopsList: React.FC<TodayStopsListProps> = ({ stops }) => {
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-info/10 text-info-text">
              <Navigation className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Today's Stops ({stops.length})
              </span>
              <div className="text-data-xs text-on-surface-subtle">Scheduled route & checkpoints</div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate(ROUTES.visits)}
            className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
          >
            <span>View Route</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>

        {/* Stops List */}
        {stops.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle py-4">
            No scheduled stops plotted for today yet.
          </div>
        ) : (
          <div className="my-2 flex flex-col divide-y divide-border">
            {stops.map((stop, idx) => {
              const isCompleted = stop.status === 'completed';
              const isCurrent = stop.status === 'in_progress';

              return (
                <div
                  key={stop.id}
                  className="flex items-center justify-between py-2.5 transition hover:bg-surface-variant px-2 rounded-lg"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-body-sm font-semibold shrink-0 mt-0.5 ${
                        isCompleted
                          ? 'bg-emerald-100 text-success-text'
                          : isCurrent
                          ? 'bg-primary text-on-primary ring-2 ring-primary/20 animate-pulse'
                          : 'bg-surface-container text-on-surface-variant'
                      }`}
                    >
                      {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : idx + 1}
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-body-sm text-on-surface">{stop.time}</span>
                        <span className="font-medium text-body-sm text-on-surface">{stop.client}</span>
                      </div>
                      <div className="text-data-xs text-on-surface-subtle">{stop.purpose}</div>
                      <div className="text-data-xs text-on-surface-subtle">{stop.location}</div>
                    </div>
                  </div>

                  <span
                    className={`rounded-md px-2 py-0.5 text-data-xs font-semibold capitalize ${
                      isCompleted
                        ? 'bg-success/10 text-success-text'
                        : isCurrent
                        ? 'bg-primary-container text-primary border border-primary/30'
                        : 'bg-surface-container text-on-surface-variant'
                    }`}
                  >
                    {stop.status.replace('_', ' ')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Route Status:</span>
        <span className="font-semibold text-on-surface">
          {stops.filter((s) => s.status === 'completed').length} of {stops.length} stops completed
        </span>
      </div>
    </div>
  );
};
